import React, { useEffect, useState } from 'react';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, ClipboardList, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ReviewForm, { StageAnswers } from './perf/ReviewForm';
import ReportCard from './perf/ReportCard';

const dueBadge = (d) => {
  if (!d) return null;
  const overdue = d < new Date().toISOString().slice(0, 10);
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold border ${overdue ? 'bg-red-100 text-red-800 border-red-300' : 'bg-[#FFF3E0] text-[#B45309] border-[#FFD9A8]'}`}>
      {overdue ? `OVERDUE — was due ${d}` : `Due ${d}`}
    </span>
  );
};

export default function MyReviewsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/perf/my-tasks').then(r => setData(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (answers) => {
    setBusy(true);
    try {
      await api.post(`/perf/reviews/${task.review.id}/submit`, { stage: task.kind, answers });
      toast.success(task.kind === 'self' ? 'Self-review submitted — thank you!' : 'Manager review submitted');
      setTask(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    setBusy(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-4xl" data-testid="my-reviews-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <ClipboardList className="h-7 w-7 text-[#6B3FA0]" /> My Reviews
        </h1>
        <p className="text-sm text-[#8E85A0] mt-1">Reviews waiting on you, and summaries you've received.</p>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-black uppercase tracking-wider text-[#9B8FAE]">To complete ({(data?.tasks || []).length})</p>
        {(data?.tasks || []).map(t => (
          <button key={`${t.kind}-${t.review.id}`} onClick={() => setTask(t)} data-testid={`task-${t.kind}-${t.review.id}`}
                  className="w-full flex items-center gap-3 rounded-xl border border-[#DED8EB] bg-white px-4 py-3 text-left hover:border-[#6B3FA0] transition-colors">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${t.kind === 'self' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
              {t.kind === 'self' ? 'SELF-REVIEW' : 'MANAGER REVIEW'}
            </span>
            <div className="flex-1">
              <p className="text-sm font-bold text-[#2D1B4E]">
                {t.kind === 'self' ? t.review.period : `${t.review.employee_name} — ${t.review.period}`}
              </p>
              <p className="text-[11px] text-[#8E85A0]">
                {t.kind === 'self' ? 'Rate your own performance this period' : 'Their self-review is in — complete your manager review'}
              </p>
            </div>
            {dueBadge(t.review.due_date)}
            <ChevronRight className="h-4 w-4 text-[#8E85A0]" />
          </button>
        ))}
        {(data?.tasks || []).length === 0 && <p className="text-sm text-[#8E85A0] rounded-xl border border-dashed border-[#DED8EB] bg-white p-5 text-center">Nothing waiting on you right now.</p>}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-black uppercase tracking-wider text-[#9B8FAE]">Report cards ({(data?.summaries || []).length})</p>
        {(data?.summaries || []).map(s => <ReportCard key={s.id} data={s} />)}
        {(data?.summaries || []).length === 0 && <p className="text-sm text-[#8E85A0] rounded-xl border border-dashed border-[#DED8EB] bg-white p-5 text-center">No report cards yet — they appear here after your in-person review.</p>}
      </div>

      <Dialog open={!!task} onOpenChange={(v) => { if (!v) setTask(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="task-dialog">
          {task && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {task.kind === 'self' ? `Self-review — ${task.review.period}` : `Manager review: ${task.review.employee_name} — ${task.review.period}`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {task.kind === 'manager' && <StageAnswers stage="self" label={`${task.review.employee_name}'s self-review`} review={task.review} />}
                <ReviewForm questions={task.review.questions} onSubmit={submit} submitting={busy}
                            submitLabel={task.kind === 'self' ? 'Submit self-review' : 'Submit manager review'} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
